import { createRef, Component } from 'react';
import { compose } from 'redux';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { loadProBySlug } from 'modules/static-pages/service';
import SupportersForPro from './SupportersForPro';
import CostInstruction from '../ProContent/CostInstruction';
import { setItem } from 'utils/sessionStorage';
import { isSmallScreen } from 'utils/layout';
import { logEvent } from 'utils/analytics';

import MainBannerWrapper from './MainBanner';
import ProAppSteps from './ProAppSteps';
import SolutionBriefing from './SolutionBriefing';
import { ProFormModal, ProForm } from './ProForm';
import ProSignUpTree from './ProSignUpTree';

import CONTENTS_EN from '../../contents/pro.en.json';
import CONTENTS_JA from '../../contents/pro.ja.json';

import styles from './ProContent.module.scss';

const CTA_OFFSET_Y = 550;
const DEBOUNCE_TIME = 300;
const MOBILE_BREAKPOINT = 768;

const loadReferralUser = async (router) => {
  const { referralSlug } = router.query;
  if (referralSlug) {
    const user = await loadProBySlug({ slug: referralSlug });
    if (user) return user.id;
  }
  return '';
};

class ProContent extends Component {
  static propTypes = {
    city: PropTypes.shape({}),
    currentUser: PropTypes.shape({}),
    content: PropTypes.shape({}).isRequired,
    language: PropTypes.string,
    prefecture: PropTypes.shape({}),
    proJobCategoriesForSignUp: PropTypes.shape({
      createdDate: PropTypes.number,
      trees: PropTypes.array.isRequired,
    }).isRequired,
    proJobType: PropTypes.string,
    router: PropTypes.any.isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    city: {},
    currentUser: undefined,
    isTestVariation: false,
    language: 'ja',
    prefecture: {},
    proJobCategoriesForSignUp: { trees: [] },
    proJobType: '依頼',
  };

  constructor(props) {
    super(props);
    this.state = {
      content: props.content,
      isFormVisible: false,
      showStickyCta: false,
      googleNonce: null,
    };
    this.isMobileDevice = true;
    this.maxScrollHeight = null;
    this.howItWorksRef = createRef();
    this.solutionBriefingRef = createRef();
    this.costInstructionRef = createRef();
    this.publishedMediaRef = createRef();
    this.faqRef = createRef();
    this.proVoicesRef = createRef();
  }

  async componentDidMount() {
    const { router } = this.props;
    const referral = await loadReferralUser(router);
    if (referral) setItem('ngStorage-referral', JSON.stringify(referral));

    this.maxScrollHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );

    this.isMobileDevice = isSmallScreen(_.get(window, 'innerWidth'));

    this.watchScroll = fromEvent(window, 'scroll')
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe(() => this.handleScroll());

    this.watchWidth = fromEvent(window, 'resize')
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe(() => this.updateDeviceType());

    setItem('httpReferrer', document.referrer || '');

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      showStickyCta: window.pageYOffset > CTA_OFFSET_Y,
    });
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ language }) {
    const { language: propsLanguage } = this.props;
    if (propsLanguage && propsLanguage !== language) {
      this.setState({ content: language === 'en' ? CONTENTS_EN : CONTENTS_JA });
    }
  }

  componentWillUnmount() {
    this.watchScroll.unsubscribe();
    this.watchWidth.unsubscribe();
  }

  handleScroll = () => {
    const currentScrollDepth = window.scrollY;
    const FOOTER_HEIGHT = 1000;
    const FORM_VIEW_HEIGHT = 470;
    const isPastSignupForm = currentScrollDepth >= FORM_VIEW_HEIGHT;
    const isInFooterView = this.maxScrollHeight
      ? currentScrollDepth > this.maxScrollHeight - FOOTER_HEIGHT
      : true;

    this.setState({
      showStickyCta: isPastSignupForm && !isInFooterView,
    });
  };

  updateDeviceType = () => {
    this.isMobileDevice = window.innerWidth <= MOBILE_BREAKPOINT;
  };

  toggleProForm = (show) => {
    this.setState({
      isFormVisible: show,
    });
  };

  openProForm = async () => {
    // eslint-disable-next-line react/prop-types
    const { currentUser, router } = this.props;
    const language = router.query.language || 'ja';
    const langPath = language === 'en' ? '/en' : '';
    // eslint-disable-next-line react/prop-types
    if (currentUser?.profileComplete) window.location.href = `${langPath}/account/view-profile`;
    else {
      logEvent(`proSignUp`, `startedRegistration`, `newProPage`);
      await router.push('/sign-up/pro');
    }
  };

  render() {
    const { content, isFormVisible, showStickyCta } = this.state;
    const { t, proJobType, proJobCategoriesForSignUp, currentUser } = this.props;

    const isLoggedIn = Boolean(currentUser);
    return (
      <div className={styles.container}>
        <MainBannerWrapper
          t={t}
          proJobType={proJobType}
          // eslint-disable-next-line react/jsx-no-bind
          toggleProForm={(show) => this.toggleProForm(show)}
        />

        <section
          className={classNames(styles['section'], styles['solution-briefing'])}
          data-test-id="solution-briefing-container"
          ref={this.solutionBriefingRef}
        >
          <SolutionBriefing isLoggedIn={isLoggedIn} />
        </section>
        {!this.isMobileDevice && (
          <section
            className={classNames(styles['section'], styles['pro-sign-up-form'], {
              [styles['logged-in']]: isLoggedIn,
            })}
          >
            <h2 className={styles['pro-sign-up-title']}>{t('staticPages:pro.signUpForm.title')}</h2>
            <div className={styles['pro-form']}>
              <ProForm
                t={t}
                isMobileDevice={false}
                proJobType={proJobType}
                // eslint-disable-next-line react/jsx-no-bind
                toggleProForm={(show) => this.toggleProForm(show)}
              />
            </div>
          </section>
        )}
        <section
          className={classNames(styles['section'], styles['how-it-works'])}
          data-test-id="how-it-works-container"
          ref={this.howItWorksRef}
        >
          <ProAppSteps proAppSteps={content.proAppSteps} t={t} />
        </section>
        <section
          className={classNames(styles['section'], styles['cost-instruction'])}
          data-test-id="cost-instruction-container"
          ref={this.costInstructionRef}
        >
          <CostInstruction />
        </section>
        {!this.isMobileDevice && (
          <section
            className={classNames(styles['section'], styles['pro-sign-up-form'], {
              [styles['logged-in']]: isLoggedIn,
            })}
          >
            <h2 className={styles['pro-sign-up-title']}>{t('staticPages:pro.signUpForm.title')}</h2>
            <div className={styles['pro-form']}>
              <ProForm
                t={t}
                isMobileDevice={false}
                proJobType={proJobType}
                // eslint-disable-next-line react/jsx-no-bind
                toggleProForm={(show) => this.toggleProForm(show)}
              />
            </div>
          </section>
        )}
        <section className={classNames(styles['section'], styles['supporters'])}>
          <SupportersForPro />
        </section>
        {isFormVisible && (
          <ProFormModal
            // eslint-disable-next-line react/jsx-no-bind
            toggleProForm={(show) => this.toggleProForm(show)}
            // eslint-disable-next-line react/jsx-no-bind
            onClose={() => this.toggleProForm(false)}
            isFormVisible={isFormVisible}
            isMobileDevice={this.isMobileDevice}
            showStickyCta={showStickyCta}
          />
        )}
        {proJobCategoriesForSignUp.trees.length > 0 && (
          <section className={classNames(styles['section'], styles['pro-sign-up-categories'])}>
            <h2>カテゴリーから探す</h2>
            <div className={styles['caption']}>提供サービスは ご登録後も追加・変更可能です</div>
            <ProSignUpTree data={proJobCategoriesForSignUp} />
          </section>
        )}
        {this.isMobileDevice && showStickyCta && (
          <div className={styles['fixed-sticky-container']}>
            <button
              className={classNames(styles['get-started-btn'], 'pro-page-cta-btn')}
              onClick={this.openProForm}
            >
              {t('staticPages:pro.form.start')}
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default compose(withTranslation(['common', 'staticPages']), withRouter)(ProContent);
